/**
 * IndexedDB wrapper for audio blob caching.
 *
 * Stores:
 * - Audio blobs during upload (persist across chat switches / refresh)
 * - Processed audio metadata (duration) for instant playback readiness
 *
 * DB: "efone-audio-cache", object store: "blobs"
 */

const DB_NAME = "efone-audio-cache";
const DB_VERSION = 1;
const STORE_NAME = "blobs";

interface AudioCacheEntry {
  /** Upload task ID or message ID */
  id: string;
  blob: Blob;
  chatId: string;
  duration?: number;
  createdAt: number;
  /** "uploading" | "uploaded" | "failed" */
  status: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("chatId", "chatId", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function withStore(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<any> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

const audioCacheDB = {
  /** Store an audio blob (during or after recording, before upload completes). */
  async put(
    id: string,
    blob: Blob,
    chatId: string,
    status = "uploading",
    duration?: number,
  ): Promise<void> {
    const entry: AudioCacheEntry = {
      id,
      blob,
      chatId,
      status,
      duration,
      createdAt: Date.now(),
    };
    await withStore("readwrite", (store) => store.put(entry));
  },

  /** Retrieve a cached audio entry. */
  async get(id: string): Promise<AudioCacheEntry | undefined> {
    return withStore("readonly", (store) => store.get(id));
  },

  /** Update just the status (e.g. uploading → uploaded). */
  async updateStatus(id: string, status: string): Promise<void> {
    const entry = await this.get(id);
    if (entry) {
      entry.status = status;
      await withStore("readwrite", (store) => store.put(entry));
    }
  },

  /** Delete a cached entry (after upload succeeds and server URL is available). */
  async remove(id: string): Promise<void> {
    await withStore("readwrite", (store) => store.delete(id));
  },

  /** Get all entries for a given chat. */
  async getByChatId(chatId: string): Promise<AudioCacheEntry[]> {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, "readonly");
          const store = tx.objectStore(STORE_NAME);
          const index = store.index("chatId");
          const req = index.getAll(chatId);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
          tx.oncomplete = () => db.close();
        }),
    );
  },

  /** Purge entries older than maxAge ms (default: 24 hours). */
  async cleanup(maxAge = 24 * 60 * 60 * 1000): Promise<void> {
    const cutoff = Date.now() - maxAge;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const entry = cursor.value as AudioCacheEntry;
          if (entry.createdAt < cutoff) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },
};

export default audioCacheDB;
